import React, { forwardRef } from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline' | 'black';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  isLoading?: boolean;
  icon?: React.ReactNode;
  fullWidth?: boolean;
}

// Use React.forwardRef to allow the component to accept a ref and pass it to the underlying button element
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  isLoading = false,
  icon,
  className = '',
  fullWidth = false,
  ...props 
}, ref) => {
  const baseStyles = "relative inline-flex items-center justify-center font-semibold tracking-tight transition-all duration-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 overflow-hidden whitespace-nowrap rounded-full backdrop-blur-sm";
  
  const variants = {
    // LOGO 同款渐变（默认主色）
    primary: "text-slate-950 border-0 shadow-lg hover:shadow-xl bg-[linear-gradient(135deg,#c99c2e,#6b8a9e,#f2e7d5,#c99c2e)] bg-[length:200%_auto] animate-text-shine",
    
    // 🔥 黑曜石/深海蓝渐变：高质感暗色按钮
    black: "bg-gradient-to-br from-slate-900 to-slate-800 text-slate-100 shadow-[0_4px_12px_rgba(15,23,42,0.3)] hover:shadow-[0_8px_24px_rgba(15,23,42,0.4)] border border-white/10 hover:border-white/20 active:shadow-inner active:bg-slate-900",
    
    secondary: "bg-white/80 text-[#0f172a] border border-slate-200/70 shadow-sm hover:border-slate-300 hover:bg-white",
    outline: "bg-transparent text-[#0f172a] border border-slate-300 hover:bg-white hover:border-slate-400",
    ghost: "bg-transparent text-[#0f172a] hover:bg-slate-100/60",
  };

  const sizes = {
    sm: "text-xs px-4 py-1.5 gap-1.5",
    md: "text-sm px-6 py-2.5 gap-2", 
    lg: "text-base px-8 py-3.5 gap-2.5",
    icon: "p-2 aspect-square",
  };

  return (
    <button 
      ref={ref}
      className={`
        ${baseStyles} 
        ${variants[variant]} 
        ${sizes[size]} 
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      disabled={isLoading || props.disabled}
      {...props}
    >
      {/* 动态光晕效果 */}
      {variant === 'black' && !isLoading && (
        <span className="absolute inset-0 rounded-full animate-breathe pointer-events-none ring-4 ring-amber-400/20"></span>
      )}
      
      {isLoading ? (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      ) : icon ? icon : null}
      {children && <span className="relative z-10">{children}</span>}
    </button>
  );
});

Button.displayName = 'Button';
